import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '../../../../contexts/authContext';
import { registryKeys } from '../../queryKeys';
import { registryRecordClient } from '../registryRecordClient';
import { plural } from './model';
export const lcatAnchorKeys = {
    all: [
        'registry',
        'lcat-anchor'
    ],
    dossier: (viewer, id)=>[
            ...lcatAnchorKeys.all,
            viewer,
            id,
            'dossier'
        ],
    points: (viewer, id, fy)=>[
            ...lcatAnchorKeys.all,
            viewer,
            id,
            'points',
            ...fy
        ],
    people: (viewer, id)=>[
            ...lcatAnchorKeys.all,
            viewer,
            id,
            'people'
        ]
};
/** Dossier, plotted points (per fiscal-year selection) and people for one anchor. */ export function useLaborCategoryAnchor(anchorId, fiscalYears) {
    const { user } = useAuth();
    const viewer = `${user?.tenant_id ?? ''}:${user?.id ?? ''}`;
    const enabled = !!user?.tenant_id && !!user?.id && !!anchorId;
    const dossier = useQuery({
        queryKey: lcatAnchorKeys.dossier(viewer, anchorId),
        enabled,
        retry: false,
        queryFn: ()=>registryRecordClient.getLaborCategoryAnchorDossier({
                anchorId
            })
    });
    const relatedEnabled = enabled && !!dossier.data;
    const points = useQuery({
        queryKey: lcatAnchorKeys.points(viewer, anchorId, fiscalYears),
        enabled: relatedEnabled,
        retry: false,
        queryFn: ()=>registryRecordClient.listAnchorRatePoints({
                anchorId,
                fiscalYears
            })
    });
    const people = useQuery({
        queryKey: lcatAnchorKeys.people(viewer, anchorId),
        enabled: relatedEnabled,
        retry: false,
        queryFn: ()=>registryRecordClient.listAnchorPeople({
                anchorId
            })
    });
    return {
        dossier,
        points,
        people
    };
}
/** REG-50: asks the suggester for anchor proposals for one entry; they land in the review queue. */ export function useSuggestMappingsForEntry() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (entryId)=>registryRecordClient.suggestMappingsForEntry({
                entryId
            }),
        onSuccess: (resp)=>{
            void qc.invalidateQueries({
                queryKey: [
                    'lcat mappings'
                ]
            });
            void qc.invalidateQueries({
                queryKey: [
                    ...registryKeys.all,
                    'review-queue'
                ]
            });
            void qc.invalidateQueries({
                queryKey: lcatAnchorKeys.all
            });
            if (resp.proposed > 0) toast.success(`${plural(resp.proposed, 'suggestion')} sent to the review queue.`);
            else toast.info(resp.message || 'No confident match.');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Could not ask for suggestions');
        }
    });
}
